import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import { useTeamStore } from "../state/teamStore";
import { useAuthStore } from "../state/authStore";
import { api } from "../utils/api";
import type { TrendsResponse, TrendWeekData } from "../types";

/* ─────────────────────────────────────────────────────────────────────────────
   Trends — one driver's lap-time improvement across a season's weeks (#219).
   A real time-series line per metric (Best Lap hero), not a rank-vs-team bar.
   ───────────────────────────────────────────────────────────────────────────── */

const LINE = "#5b8def"; // --live
const AXIS = "#8d94a3"; // --text-secondary
const GRID = "rgba(141, 148, 163, 0.12)";
const TICK_FONT = '500 11px "Geist", system-ui, sans-serif';
const LABEL_FONT = '600 12px "Geist", system-ui, sans-serif';

type MetricKey = "bestLap" | "top10Avg" | "consistency" | "greenLaps";
interface Metric {
  key: MetricKey;
  label: string;
  lowerBetter: boolean;
  get: (w: TrendWeekData) => number | null;
  fmt: (v: number) => string;
  axisFmt: (v: number) => string;
}

function fmtClock(v: number, d: number): string {
  const m = Math.floor(v / 60);
  const s = (v % 60).toFixed(d);
  return `${m}:${s.padStart(d > 0 ? 3 + d : 2, "0")}`;
}

const METRICS: Metric[] = [
  { key: "bestLap", label: "Best Lap", lowerBetter: true, get: (w) => w.bestLap, fmt: (v) => fmtClock(v, 3), axisFmt: (v) => fmtClock(v, 1) },
  { key: "top10Avg", label: "Top 10 Avg", lowerBetter: true, get: (w) => w.top10Avg, fmt: (v) => fmtClock(v, 3), axisFmt: (v) => fmtClock(v, 1) },
  { key: "consistency", label: "Consistency", lowerBetter: true, get: (w) => w.consistency, fmt: (v) => `${v.toFixed(3)}s`, axisFmt: (v) => `${v.toFixed(2)}s` },
  { key: "greenLaps", label: "Green Laps", lowerBetter: false, get: (w) => w.greenLaps, fmt: (v) => `${Math.round(v)}`, axisFmt: (v) => `${Math.round(v)}` },
];

function humanizeSeason(code: string): string {
  const m = /^(\d{2})S([1-4])$/.exec(code);
  return m ? `20${m[1]} Season ${m[2]}` : code;
}

function describeDelta(m: Metric, delta: number): string {
  const mag = Math.abs(delta);
  if (m.key === "greenLaps") return `${delta > 0 ? "up" : "down"} ${Math.round(mag)}`;
  // For lower-is-better metrics, a negative delta is the improvement.
  const dir = m.lowerBetter ? (delta < 0 ? "down" : "up") : (delta > 0 ? "up" : "down");
  return `${dir} ${mag.toFixed(3)}s`;
}

export default function Trends() {
  const { activeTeamId } = useTeamStore();
  const user = useAuthStore((s) => s.user);
  const [seasons, setSeasons] = useState<string[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<string | null>(null);
  const [trends, setTrends] = useState<TrendsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<string>("");
  const [metricKey, setMetricKey] = useState<MetricKey>("bestLap");

  useEffect(() => {
    if (!activeTeamId) return;
    api<{ seasons: string[] }>(`/api/comparison/trends/seasons?team_id=${activeTeamId}`)
      .then((res) => {
        setSeasons(res.seasons);
        setSelectedSeason((prev) => prev ?? (res.seasons.length ? res.seasons[res.seasons.length - 1] : null));
      })
      .catch(() => setSeasons([]));
  }, [activeTeamId]);

  useEffect(() => {
    if (!activeTeamId || !selectedSeason) return;
    setLoading(true);
    api<TrendsResponse>(`/api/comparison/trends?team_id=${activeTeamId}&season=${encodeURIComponent(selectedSeason)}`)
      .then((res) => { setTrends(res); setLoading(false); })
      .catch(() => { setTrends(null); setLoading(false); });
  }, [activeTeamId, selectedSeason]);

  const driverIds = useMemo(() => (trends ? Object.keys(trends.drivers) : []), [trends]);

  // Default to the signed-in driver, else the first in the lineup.
  useEffect(() => {
    if (driverIds.length === 0) { setSelectedDriver(""); return; }
    setSelectedDriver((prev) => {
      if (prev && driverIds.includes(prev)) return prev;
      if (user?.id && driverIds.includes(user.id)) return user.id;
      return driverIds[0];
    });
  }, [driverIds, user?.id]);

  const metric = METRICS.find((m) => m.key === metricKey) ?? METRICS[0];
  const driver = selectedDriver ? trends?.drivers[selectedDriver] : undefined;

  const chartWeeks = useMemo(() => {
    if (!driver) return [];
    return driver.weeks
      .slice()
      .sort((a, b) => (a.weekNumber ?? 0) - (b.weekNumber ?? 0))
      .map((w) => ({ n: w.weekNumber ?? 0, v: metric.get(w) }));
  }, [driver, metric]);

  const present = chartWeeks.filter((w) => w.v != null);
  const delta = present.length >= 2 ? (present[present.length - 1].v as number) - (present[0].v as number) : null;
  const improved = delta != null && (metric.lowerBetter ? delta < 0 : delta > 0);
  const worsened = delta != null && (metric.lowerBetter ? delta > 0 : delta < 0);
  const deltaColor = improved ? "var(--flag-green)" : worsened ? "var(--flag-red)" : "var(--text-muted)";

  return (
    <div>
      <div style={st.controls}>
        <div style={st.driverWrap}>
          {driverIds.length > 1 && trends && (
            <select style={st.select} value={selectedDriver} onChange={(e) => setSelectedDriver(e.target.value)}>
              {driverIds.map((id) => (
                <option key={id} value={id}>{trends.drivers[id].displayName || "Unknown"}</option>
              ))}
            </select>
          )}
        </div>
        <div style={st.seasonWrap}>
          <span style={st.seasonLabel}>Season</span>
          <select style={st.select} value={selectedSeason || ""} onChange={(e) => setSelectedSeason(e.target.value)}>
            {seasons.length === 0 && <option value="">No seasons</option>}
            {seasons.map((s) => (
              <option key={s} value={s}>{humanizeSeason(s)}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="empty-state">
          <div className="spinner spinner-lg" />
          <div className="empty-state-title">Loading trends</div>
        </div>
      ) : !driver || chartWeeks.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">No trend data</div>
          <div className="empty-state-text">Upload runs across a few weeks of {selectedSeason ? humanizeSeason(selectedSeason) : "a season"} to see a driver's progression.</div>
        </div>
      ) : (
        <div className="card" style={st.card}>
          <div style={st.head}>
            <div>
              <div style={st.driverName}>{driver.displayName || "Unknown driver"}</div>
              <div style={st.deltaLine}>
                {present.length < 2 ? (
                  <span style={{ color: "var(--text-muted)" }}>Needs two weeks to show a trend.</span>
                ) : (
                  <span style={{ color: deltaColor, fontWeight: 700 }}>
                    {metric.label} {describeDelta(metric, delta as number)} since Week {present[0].n}
                  </span>
                )}
              </div>
            </div>
            <div style={st.toggle} role="group" aria-label="Metric">
              {METRICS.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setMetricKey(m.key)}
                  aria-pressed={m.key === metricKey}
                  style={{ ...st.toggleBtn, ...(m.key === metricKey ? st.toggleBtnActive : null) }}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          {metric.key === "consistency" && (
            <div style={st.caption}>Consistency is lap-to-lap spread. Lower is better.</div>
          )}
          <TrendChart key={`${selectedDriver}-${metricKey}`} weeks={chartWeeks} metric={metric} />
        </div>
      )}
    </div>
  );
}

function TrendChart({ weeks, metric, height = 320 }: { weeks: { n: number; v: number | null }[]; metric: Metric; height?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const chartRef = useRef<uPlot | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    const vals = weeks.map((w) => w.v).filter((v): v is number => v != null);
    if (vals.length === 0) return;
    const xData = weeks.map((w) => w.n);
    const yData = weeks.map((w) => w.v);
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const pad = Math.max((hi - lo) * 0.15, Math.abs(hi) * 0.004, 0.001);

    const opts: uPlot.Options = {
      width: ref.current.clientWidth || 800,
      height,
      padding: [16, 12, 0, 12],
      cursor: { x: true, y: false, drag: { x: false, y: false, setScale: false } },
      scales: { x: { time: false }, y: { range: () => [lo - pad, hi + pad] } },
      axes: [
        {
          stroke: AXIS, grid: { stroke: GRID }, ticks: { stroke: GRID }, font: TICK_FONT, labelFont: LABEL_FONT, label: "Week",
          splits: (_u, _a, min, max) => {
            const out: number[] = [];
            for (let n = Math.ceil(min); n <= Math.floor(max); n++) out.push(n);
            return out;
          },
          values: (_u, vs) => vs.map((v) => `W${v}`),
        },
        { stroke: AXIS, grid: { stroke: GRID }, ticks: { stroke: GRID }, font: TICK_FONT, values: (_u, vs) => vs.map((v) => metric.axisFmt(v)) },
      ],
      series: [
        { label: "Week", value: (_u, v) => (v == null ? "-" : `W${v}`) },
        { label: metric.label, stroke: LINE, width: 2, points: { show: true, size: 7 }, value: (_u, v) => (v == null ? "-" : metric.fmt(v)) },
      ],
      legend: { show: true, live: true },
    };

    if (chartRef.current) chartRef.current.destroy();
    chartRef.current = new uPlot(opts, [xData, yData] as uPlot.AlignedData, ref.current);
    const onResize = () => {
      if (chartRef.current && ref.current) chartRef.current.setSize({ width: ref.current.clientWidth, height });
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [weeks, metric, height]);

  return (
    <>
      <style>{`
        .xp-trend .u-legend { font-family: var(--font-mono); font-size: 13px; }
        .xp-trend .u-legend .u-value { font-weight: 700; color: var(--text-strong); }
        .xp-trend .u-legend .u-label { color: var(--text-secondary); }
        .xp-trend .u-cursor-x { border-left: 1px dashed rgba(141,148,163,0.55) !important; width: 0 !important; }
      `}</style>
      <div ref={ref} className="xp-trend" style={{ width: "100%" }} />
    </>
  );
}

const st: Record<string, CSSProperties> = {
  controls: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", marginBottom: "1.25rem", flexWrap: "wrap" },
  driverWrap: { display: "flex", alignItems: "center", gap: "0.5rem" },
  seasonWrap: { display: "flex", alignItems: "center", gap: "0.5rem", marginLeft: "auto" },
  seasonLabel: { fontFamily: "var(--font-body)", fontSize: "0.82rem", color: "var(--text-secondary)" },
  select: {
    fontFamily: "var(--font-body)", fontSize: "0.88rem", fontWeight: 600,
    padding: "0.4rem 0.7rem", borderRadius: "6px",
    border: "1px solid var(--rule)", background: "var(--ink-input)", color: "var(--text-strong)",
  },
  card: { padding: "1.25rem" },
  head: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", marginBottom: "0.85rem", flexWrap: "wrap" },
  driverName: { fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "1.1rem", color: "var(--text-strong)" },
  deltaLine: { fontFamily: "var(--font-body)", fontSize: "0.9rem", marginTop: "0.2rem" },
  toggle: { display: "inline-flex", border: "1px solid var(--rule)", borderRadius: "7px", overflow: "hidden", flexWrap: "wrap" },
  toggleBtn: {
    fontFamily: "var(--font-body)", fontSize: "0.8125rem", fontWeight: 600,
    padding: "0.35rem 0.75rem", cursor: "pointer", border: "none",
    background: "var(--ink-input)", color: "var(--text-secondary)", transition: "all 0.15s ease",
  },
  toggleBtnActive: { background: "var(--live-soft)", color: "var(--live)" },
  caption: { fontFamily: "var(--font-body)", fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: "0.4rem" },
};
